// GarmentInspector — the card for a garment_assets row (ENG-1108).
//
// A garment is stored as authored: a Marvelous Designer glTF with no skin,
// bound to the character's body only when it loads. So there is nothing here to
// edit that changes the mesh — the editable part is its NAME, and the rest is a
// report of what the importer measured.
//
// The two numbers that matter are the ones a bad import hides behind a
// plausible-looking render: how much of the mesh found a body under it, and
// whether the garment is even the right size for this character. Both come from
// `window.__GARMENT__` when this garment is the one currently worn, so the card
// shows live truth rather than something recorded at upload time and possibly
// stale (garmentAttach.js).

import React, { useEffect, useState } from 'react'
import { CustomAssetManager } from '../../lib/CustomAssetManager'
import { garmentState, onGarmentChange } from '../../lib/garment/garmentAttach.js'
import { UNBOUND_WARN_PCT } from '../../lib/garment/garmentIndicatorState.js'
import {
  GARMENT_OPTION_FIELDS, readGarmentOptions, writeGarmentOptions,
  hasSavedGarmentOptions, describeGarmentOptions,
} from '../../lib/garment/garmentImportOptions.js'
import DirtyBadge from '../DirtyBadge'

const C = {
  border: 'var(--m-line)',
  text: 'var(--m-fg)',
  dim: 'var(--m-fg-2)',
  ok: 'var(--m-online)',
  warn: 'var(--m-warn)',
}

function fmtSize(bytes) {
  if (bytes == null || bytes === 0) return '—'
  const units = ['B', 'KB', 'MB', 'GB']
  let n = Number(bytes), i = 0
  while (n >= 1024 && i < units.length - 1) { n /= 1024; i++ }
  return `${n.toFixed(n >= 10 ? 0 : 1)} ${units[i]}`
}

function Row({ label, value, tone }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, padding: '3px 0', fontSize: 11 }}>
      <span style={{ color: C.dim }}>{label}</span>
      <span style={{ color: tone ?? C.text, textAlign: 'right', maxWidth: '62%' }}>{value}</span>
    </div>
  )
}

export default function GarmentInspector({ asset }) {
  const [name, setName] = useState(asset?.name ?? '')
  const [state, setState] = useState('clean')   // clean | saving | error
  const [error, setError] = useState(null)
  const [live, setLive] = useState(garmentState)

  const [opts, setOpts] = useState(() => readGarmentOptions(asset))
  const [optState, setOptState] = useState('clean')  // clean | saving | error

  useEffect(() => { setName(asset?.name ?? ''); setState('clean'); setError(null) }, [asset?.id])
  useEffect(() => { setOpts(readGarmentOptions(asset)); setOptState('clean') }, [asset?.id])
  useEffect(() => onGarmentChange(setLive), [])

  // The live report belongs to this row only when THIS garment is the one on
  // the avatar. Showing another garment's numbers here would be worse than
  // showing none — they would look like this one's.
  const isWorn = live?.phase === 'ready' && live?.name && asset?.name &&
    (live.name === asset.name || live.name === asset.rawId)

  const meta = asset?.metadata ?? asset?.raw?.metadata ?? {}

  /**
   * Save one switch. Optimistic on screen and LOUD on failure: the checkbox
   * snaps back to what the server actually holds, because a toggle that looks
   * set and is not would change how the garment loads for everyone else.
   */
  async function toggleOption(key, value) {
    const prev = opts
    const next = { ...opts, [key]: value }
    setOpts(next)
    setOptState('saving')
    try {
      const row = await CustomAssetManager.updateGarmentAsset(
        asset.rawId,
        { metadata: writeGarmentOptions(asset, next) },
        { version: asset.version },
      )
      // Believe the row the server echoed, not the value we sent — the same
      // rule the folder write follows (assetFolderPath.js).
      setOpts(readGarmentOptions(row))
      setOptState('clean')
    } catch (e) {
      setOpts(prev)
      setOptState('error')
      setError(String(e?.message ?? e))
    }
  }

  async function commitName() {
    const next = name.trim()
    if (!next || next === asset.name) { setState('clean'); return }
    setState('saving'); setError(null)
    try {
      await CustomAssetManager.updateGarmentAsset(asset.rawId, { name: next }, { version: asset.version })
      setState('clean')
    } catch (e) {
      // Loud: the field keeps the user's text and the badge says it did not land.
      setState('error')
      setError(String(e?.message ?? e))
    }
  }

  return (
    <div style={{ padding: 10, color: C.text, fontFamily: 'var(--m-font)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <input
          data-testid="garment-name"
          value={name}
          onChange={e => { setName(e.target.value); setState('dirty') }}
          onBlur={commitName}
          onKeyDown={e => { if (e.key === 'Enter') e.currentTarget.blur() }}
          style={{
            flex: 1, background: 'var(--m-panel)', color: C.text,
            border: `1px solid ${C.border}`, borderRadius: 3, padding: '4px 6px', fontSize: 12,
          }}
        />
        <DirtyBadge state={state} />
      </div>
      {error && <div style={{ color: 'var(--m-danger)', fontSize: 10, marginBottom: 6 }}>{error}</div>}

      <Row label="format" value={(asset?.format ?? '—').toUpperCase()} />
      <Row label="size" value={fmtSize(asset?.byteSize)} />
      <Row label="folder" value={asset?.serverFolderPath || '/'} />
      {meta.pieces != null && <Row label="pattern pieces" value={meta.pieces} />}
      {meta.vertices != null && <Row label="vertices" value={Number(meta.vertices).toLocaleString()} />}

      <div style={{ borderTop: `1px solid ${C.border}`, marginTop: 8, paddingTop: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
          <span style={{ color: C.dim, fontSize: 10, letterSpacing: 0.5 }}>WHEN APPLIED</span>
          <DirtyBadge state={optState} />
          {hasSavedGarmentOptions(asset) && (
            <span style={{ color: C.dim, fontSize: 9 }} title="This garment has its own settings; a garment that says nothing loads on the defaults.">
              {'✱'}
            </span>
          )}
        </div>
        {GARMENT_OPTION_FIELDS.map(f => (
          <label key={f.key} title={f.note ?? undefined}
            style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '2px 0', fontSize: 11, color: C.text, cursor: 'pointer' }}>
            <input
              type="checkbox"
              data-testid={`garment-option-${f.key}`}
              checked={opts[f.key]}
              disabled={optState === 'saving'}
              onChange={e => toggleOption(f.key, e.target.checked)}
            />
            <span>{f.label}</span>
          </label>
        ))}
        {/* Saved on the GARMENT, so every apply from the card menu uses them.
            A `loadGarment` node and a `?garment=` link still override — each
            states its own three (garmentImportOptions.js). */}
        <div style={{ color: C.dim, fontSize: 10, marginTop: 4, lineHeight: 1.5 }}>
          {describeGarmentOptions(opts)}
        </div>
      </div>

      <div style={{ borderTop: `1px solid ${C.border}`, marginTop: 8, paddingTop: 8 }}>
        {isWorn ? (
          <>
            <Row
              label="bound to"
              value={live.skin?.meshes?.join(', ') ?? '—'}
            />
            <Row
              label="unbound"
              value={`${live.unboundPct?.toFixed(1)}%`}
              tone={live.unboundPct > UNBOUND_WARN_PCT ? C.warn : C.ok}
            />
            <Row label="furthest bind" value={`${live.worstBindCm?.toFixed(1)} cm`} />
            <Row
              label="fit"
              value={live.fit?.verdict ?? '—'}
              tone={live.fit?.verdict === 'fits' ? C.ok : C.warn}
            />
            {live.fit?.summary && (
              <div style={{ color: C.dim, fontSize: 10, marginTop: 4, lineHeight: 1.5 }}>
                {live.fit.summary}
              </div>
            )}
          </>
        ) : (
          // Not worn: say so, rather than leaving a blank panel that reads as
          // "measured nothing".
          <div style={{ color: C.dim, fontSize: 10, lineHeight: 1.5 }}>
            Not currently on the avatar — load it to see the bind report and whether it fits this body.
          </div>
        )}
      </div>
    </div>
  )
}
